import useStaffData from "@app/data-hooks/use-staff-data.ts";
import { Checkbox, FormControlLabel, Stack } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { QueryStatusRouter, Select } from "bi-lib";
import { useEffect, useRef } from "react";

interface Props {
    id?: number;
    setOnSave: (onSave: () => Promise<unknown>) => void;
}

export default function NotificationForm({ id, setOnSave }: Props) {
    const { saveNotification, staff } = useStaffData({ id });

    const form = useForm({
        defaultValues: {
            numberDaysReminderEmail: staff.data?.numberDaysReminderEmail,
            send15thReminderEmail: staff.data?.send15thReminderEmail,
            sendLastDayReminderEmail: staff.data?.sendLastDayReminderEmail,
        },
        onSubmit: async ({ value }) =>
            await saveNotification.mutateAsync({
                staffingId: id,
                numberDaysReminderEmail: value.numberDaysReminderEmail,
                send15thReminderEmail: value.send15thReminderEmail,
                sendLastDayReminderEmail: value.sendLastDayReminderEmail,
            }),
    });

    const ref = useRef<HTMLFormElement>(null);

    const handleSave = async () => {
        if (ref.current?.reportValidity()) {
            await form.handleSubmit();
            return;
        }
        throw new Error("Form is invalid");
    };

    useEffect(() => {
        setOnSave(handleSave);
    }, [form]);

    return (
        <form
            ref={ref}
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void form.handleSubmit();
            }}
        >
            <QueryStatusRouter queryHook={staff}>
                <Stack spacing={1}>
                    <form.Field name={"numberDaysReminderEmail"}>
                        {(field) => (
                            <Select
                                label={"Receive missing time emails?"}
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e)}
                                fullWidth
                                options={[
                                    { label: "Never", value: 0 },
                                    { label: "Every 2 Days", value: 2 },
                                    { label: "Every 5 Days", value: 5 },
                                    { label: "Every 10 Days", value: 10 },
                                ]}
                            />
                        )}
                    </form.Field>
                    <form.Field name={"send15thReminderEmail"}>
                        {(field) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={field.state.value ?? false}
                                        onChange={(e) => field.handleChange(e.target.checked)}
                                    />
                                }
                                label={"Receive time entry reminder on the 15th?"}
                            />
                        )}
                    </form.Field>
                    <form.Field name={"sendLastDayReminderEmail"}>
                        {(field) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={field.state.value ?? false}
                                        onChange={(e) => field.handleChange(e.target.checked)}
                                    />
                                }
                                label={"Receive time entry reminder on the last of the month?"}
                            />
                        )}
                    </form.Field>
                </Stack>
            </QueryStatusRouter>
        </form>
    );
}
